import { useCallback } from 'react';
import { graphql, useRelayEnvironment } from 'react-relay';
import braintreeBankAccount from 'braintree-web/us-bank-account';
import commitMutation from 'relay-commit-mutation-promise';
import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';
import achValidate from 'dibs-ach-form/exports/ACHValidate';
import mandateText from 'dibs-ach-form/exports/mandateTextContinue';
import { PaymentMethod } from '../../types';
import { usePaymentMethodSelectContext } from '../PaymentMethodSelectContext';
import { type SubmitResult, createSubmitError } from '../submitPaymentMethod';
import { guestWalletUpdater } from '../wallet/guestWalletUpdater';

import { submitAchVaultMutation } from './__generated__/submitAchVaultMutation.graphql';

const submitAchVaultMutation = graphql`
    mutation submitAchVaultMutation($input: UserVaultPaymentMethodInput!) {
        userVaultPaymentMethod(input: $input) {
            user {
                wallet {
                    paymentMethods {
                        currencies
                        paymentMethod {
                            paymentMethodType
                            ... on CreditCard {
                                token
                                paymentProcessor
                                cardLogo
                                cardType
                                cardExpiryMonth
                                cardExpiryYear
                                formattedTextMaskedCardNumber
                                hashedCardNumber
                            }
                            ... on PayPalAccount {
                                token
                                email
                                paymentProcessor
                            }
                            ... on ACH {
                                token
                                paymentProcessor
                                accountType
                                last4
                                accountHolderName
                                routingNumber
                                display
                            }
                            ... on ApplePay {
                                token
                                paymentProcessor
                            }
                        }
                        shippingAddresses {
                            serviceId
                        }
                    }
                }
            }
            walletPaymentMethod {
                currencies
                paymentMethod {
                    ... on ACH {
                        token
                    }
                }
                shippingAddresses {
                    serviceId
                }
            }
        }
    }
`;

export function useAchSubmit(): (paymentMethod: PaymentMethod) => Promise<SubmitResult> {
    const {
        braintreeClientToken,
        viewerId,
        billingAddress,
        mockWallet,
        userId,
        paymentMethodOptions,
        achForm,
        updateAchFormErrors,
    } = usePaymentMethodSelectContext();

    const {
        isVerifying,
        accountType,
        ownershipType,
        routingNumber,
        accountNumber,
        firstNameOnAccount,
        lastNameOnAccount,
        businessNameOnAccount,
    } = achForm;

    const { firstName, lastName, company, line1, line2, state, city, zipCode, country } =
        billingAddress || {};

    const shouldVerifyPaymentMethod = paymentMethodOptions?.ACH?.verifyPaymentMethod;

    const relayEnvironment = useRelayEnvironment();

    return useCallback(
        async paymentMethod => {
            if (!userId) {
                return createSubmitError(
                    'IMPLEMENTATION',
                    'userId is required to submit credit cards'
                );
            }

            updateAchFormErrors({});

            if (paymentMethod?.paymentMethodToken && !isVerifying) {
                // using vaulted bank account
                return { paymentMethod };
            }

            const ach = {
                accountType,
                ownershipType,
                routingNumber,
                accountNumber,
                firstNameOnAccount,
                lastNameOnAccount,
                businessNameOnAccount,
            };

            const { fieldErrors, isValid } = achValidate(ach);

            if (!isValid) {
                updateAchFormErrors(fieldErrors);

                return createSubmitError('VALIDATION', 'ACH form invalid', {
                    achErrors: fieldErrors,
                });
            }

            if (country !== 'US') {
                return createSubmitError(
                    'VALIDATION',
                    'ACH must have US as billing address country'
                );
            }

            const bankAccountClient = await braintreeBankAccount.create({
                authorization: braintreeClientToken,
            });
            const isPersonalAccount = ownershipType === 'personal';

            // format zip code if it's longer than five characters. Braintree requires a dash only for the bank account API, so we need to insert a dash after the fifth number of the zipcode
            const formattedZipCode =
                zipCode && zipCode.length > 5
                    ? [zipCode.slice(0, 5), '-', zipCode.slice(5)].join('')
                    : zipCode;

            const bankDetails = {
                firstName: isPersonalAccount ? firstNameOnAccount : null,
                lastName: isPersonalAccount ? lastNameOnAccount : null,
                businessName: isPersonalAccount ? null : businessNameOnAccount,
                routingNumber: routingNumber,
                accountNumber: accountNumber,
                accountType: accountType,
                ownershipType: ownershipType,
                billingAddress: {
                    firstName: firstName || undefined,
                    lastName: lastName || undefined,
                    company: company || undefined,
                    streetAddress: line1,
                    extendedAddress: line2 || undefined,
                    locality: city,
                    region: state,
                    postalCode: formattedZipCode,
                    countryCodeAlpha2: country,
                },
            };

            const captchaResults = await executeCaptcha({
                action: 'checkoutVaultAch',
                featureFlag: 'vaultCreditCardCaptcha',
                featureFlagV3: 'vaultCreditCardCaptchaV3',
                relayEnvironment: relayEnvironment,
            });

            const braintreeData = await bankAccountClient.tokenize({
                bankDetails,
                mandateText,
            });

            const vaultResponse = await commitMutation<submitAchVaultMutation>(relayEnvironment, {
                mutation: submitAchVaultMutation,
                variables: {
                    input: {
                        userId,
                        saveToWallet: !mockWallet,
                        mockWallet: !!mockWallet,
                        paymentMethodNonce: braintreeData?.nonce,
                        verifyPaymentMethod: shouldVerifyPaymentMethod,
                        verificationToken: null,
                        captchaToken: captchaResults?.captchaToken,
                        captchaKey: captchaResults?.captchaKey,
                    },
                },
                updater:
                    mockWallet && viewerId
                        ? store =>
                              guestWalletUpdater<submitAchVaultMutation>(
                                  store,
                                  viewerId,
                                  userId,
                                  'userVaultPaymentMethod'
                              )
                        : null,
            });

            const vaultedPaymentMethod =
                vaultResponse?.userVaultPaymentMethod?.walletPaymentMethod?.paymentMethod || {};
            const paymentMethodToken = vaultedPaymentMethod?.token;

            if (!paymentMethodToken) {
                return createSubmitError('VAULT', 'Unable to get vaulted ACH token');
            }

            return {
                paymentMethod: {
                    paymentMethodType: 'ACH',
                    paymentProcessor: 'BRAINTREE',
                    paymentMethodToken,
                },
            };
        },
        [
            isVerifying,
            accountType,
            ownershipType,
            routingNumber,
            accountNumber,
            firstNameOnAccount,
            lastNameOnAccount,
            businessNameOnAccount,
            braintreeClientToken,
            firstName,
            lastName,
            company,
            line1,
            line2,
            state,
            city,
            zipCode,
            country,
            relayEnvironment,
            mockWallet,
            shouldVerifyPaymentMethod,
            updateAchFormErrors,
            userId,
            viewerId,
        ]
    );
}
